import { useState, useEffect, useCallback } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FieldHeader, TextInput } from "../../../../components/General";
import {
  LoginContainer,
  LoginButton,
  LoginGradient,
  LoginHeader,
  LoginLogo,
  LoginNav,
  LoginSubHeader,
  InnerNavContainer,
  PlainButton,
} from "../../components";
import {
  LOGIN_ADMIN_INVITE,
  adminSignUpFullPath,
} from "../../../../routes/paths";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import loginIcon from "../../../../assets/logos/login.svg";
import useLogin from "../../../../hooks/mutations/useLogin";
import { adminOnboardingFullPath } from "../../../../routes/paths";
import AuthService from "../../../../api/services/AuthService";
import { LinkConstants } from "../../../../helpers/links";

export const ADMIN_INVITE_EMAIL_URL_PARAM = "aie";

const Login = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<any>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const navigateToAdminInviteLogin = useCallback(() => {
    navigate(
      `${LOGIN_ADMIN_INVITE}?${ADMIN_INVITE_EMAIL_URL_PARAM}=${
        searchParams.get(ADMIN_INVITE_EMAIL_URL_PARAM) || ""
      }`
    );
  }, [navigate, searchParams]);

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      navigate(adminOnboardingFullPath);
    }
  }, [navigate]);

  useEffect(() => {
    if (searchParams.has(ADMIN_INVITE_EMAIL_URL_PARAM)) {
      navigateToAdminInviteLogin();
    }
  }, [navigateToAdminInviteLogin, searchParams]);

  // Mutations
  const { mutate: loginFn } = useLogin({
    onSuccessHandler: () => navigate(adminOnboardingFullPath),
  });

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Please fill out all fields");
      return;
    }

    loginFn({ email, password });
  };

  return (
    <LoginContainer>
      <LoginNav>
        <InnerNavContainer>
          <LoginHeader>Hi, Welcome back!</LoginHeader>
          <LoginSubHeader>Enter your information below</LoginSubHeader>
          <FieldHeader sx={{ margin: "40px 0 0" }}>Email</FieldHeader>
          <TextInput
            id="email-field"
            size="small"
            placeholder="Work Email"
            value={email}
            focused
            onChange={(event) => setEmail(event.target.value)}
          />
          <FieldHeader>Password</FieldHeader>
          <TextInput
            id="password-field"
            placeholder="Password"
            size="small"
            type={showPassword ? "text" : "password"}
            focused
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    size={"small"}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LoginButton children={"Login"} onClick={handleLogin}></LoginButton>
          <PlainButton
            children={"Forgot password?"}
            onClick={() => {
              window.open(LinkConstants.passwordReset, "_blank");
            }}
            sx={{ marginTop: "16px" }}
          />
          <PlainButton
            children={"Sign up"}
            onClick={() => navigate(adminSignUpFullPath)}
          />
        </InnerNavContainer>
      </LoginNav>
      <LoginGradient>
        <LoginLogo src={loginIcon} alt="logo" />
      </LoginGradient>
    </LoginContainer>
  );
};

export default Login;
